import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Loading } from "../shared/loading/Loading";
import { Alert } from "../shared/alert/Alert";
import {
  User,
  Hash,
  Info,
  FileText,
  Lock,
  CheckCircle,
} from "lucide-react";
import { useCadastroFornecedor } from "../../hooks/useFornecedores";
import avatar from "../../assets/images/avatar.jpeg";
import Style from "./index.module.css";

const DadosTab = ({ user }) => (
  <div className={Style.tabContent}>
    <img src={user.foto || avatar} alt="Avatar" className={Style.avatar} />

    <div className={Style.name}>{user.nomope || "Sem nome"}</div>

    <div className={Style.infoList}>
      <div className={Style.infoItem}>
        <Hash className={Style.infoIcon} />
        <span className={Style.infoText}>Cod. {user.codope}</span>
      </div>

      <div className={Style.infoItem}>
        <User className={Style.infoIcon} />
        <span className={Style.infoText}>{user.nomope || "Sem nome"}</span>
      </div>

      <div className={Style.infoItem}>
        <Info className={Style.infoIcon} />
        <span className={Style.infoText}>{user.tipusu}</span>
      </div>

      {user.email && (
        <div className={Style.infoItem}>
          <Info className={Style.infoIcon} />
          <span className={Style.infoText}>{user.email}</span>
        </div>
      )}

      <div className={Style.infoItem}>
        <FileText className={Style.infoIcon} />
        <a
          href="/termos-conduta.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className={Style.infoText}
          style={{ textDecoration: "underline", cursor: "pointer" }}
        >
          Termos de Conduta
        </a>
      </div>
    </div>
  </div>
);

const AlteracaoTab = ({
  passwordData,
  handlePasswordChange,
  handlePasswordSubmit,
  isLoading,
}) => (
  <div className={Style.tabContent}>
    <div className={Style.passwordSection}>
      <div className={Style.passwordHeader}>
        <Lock className={Style.passwordIcon} />
        <h3>Alterar Senha</h3>
      </div>

      <div className={Style.passwordDescription}>
        <p>Para sua segurança:</p>
        <ul className={Style.passwordTips}>
          <li>No mínimo 8 caracteres</li>
          <li>Letras maiúsculas e minúsculas</li>
          <li>Números e caracteres especiais</li>
        </ul>
      </div>

      <form onSubmit={handlePasswordSubmit} className={Style.passwordForm}>
        <div className={Style.formGroup}>
          <label htmlFor="currentPassword">
            <Lock size={18} /> Senha Atual
          </label>
          <input
            type="text"
            id="currentPassword"
            name="currentPassword"
            className={Style.passwordInput}
            placeholder="Digite sua senha atual"
            value={passwordData.currentPassword}
            onChange={handlePasswordChange}
          />
        </div>

        <div className={Style.formGroup}>
          <label htmlFor="newPassword">
            <Lock size={18} /> Nova Senha
          </label>
          <input
            type="text"
            id="newPassword"
            name="newPassword"
            className={Style.passwordInput}
            placeholder="Digite a nova senha"
            value={passwordData.newPassword}
            onChange={handlePasswordChange}
          />
        </div>

        <div className={Style.formGroup}>
          <label htmlFor="confirmPassword">
            <Lock size={18} /> Confirmar Nova Senha
          </label>
          <input
            type="text"
            id="confirmPassword"
            name="confirmPassword"
            className={Style.passwordInput}
            placeholder="Confirme a nova senha"
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange}
          />
        </div>

        <button
          type="submit"
          className={Style.submitButton}
          disabled={isLoading}
          style={{
            opacity: isLoading ? 0.7 : 1,
            cursor: isLoading ? "not-allowed" : "pointer"
          }}
        >
          <CheckCircle size={20} />
          {isLoading ? "Alterando..." : "Alterar Senha"}
        </button>
      </form>
    </div>
  </div >
);

export const Usuario = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("dados");
  const [isLoading, setIsLoading] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const { alterarSenha } = useCadastroFornecedor();

  if (!user) return <Loading text="Carregando usuário..." />;

  const isFornecedor = user.tipusu === "FORNECEDOR";

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (!currentPassword) {
      await Alert({
        icon: "warning",
        title: "Atenção",
        message: "Informe sua senha atual",
        showConfirmButton: true,
      });
      return;
    }

    if (!newPassword) {
      await Alert({
        icon: "warning",
        title: "Atenção",
        message: "Informe a nova senha",
        showConfirmButton: true,
        confirmButtonText: 'OK'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      await Alert({
        icon: "warning",
        title: "Atenção",
        message: "As senhas novas não coincidem",
        showConfirmButton: true,
        confirmButtonText: 'OK'
      });
      return;
    }

    if (newPassword.length < 8) {
      await Alert({
        icon: "warning",
        title: "Atenção",
        message: "A senha deve conter no mínimo 8 caracteres",
        showConfirmButton: true,
        confirmButtonText: 'OK'
      });
      return;
    }

    setIsLoading(true);

    try {
      await alterarSenha({
        token: user.token,
        payload: {
          senatual: currentPassword.trim(),
          sennova: newPassword.trim(),
          senconf: confirmPassword.trim(),
        }
      });

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setIsLoading(false);

      await Alert({
        icon: "success",
        title: "Sucesso",
        message: "Senha alterada com sucesso! Faça seu login novamente!",
        showConfirmButton: true,
        confirmButtonText: "OK",
        timer: 3500,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCloseButton: false
      });

      logout();
    } catch (error) {

      const message =
        error.response?.data?.erro
        || error.message
        || "Ocorreu um erro inesperado ao tentar alterar a senha.";

      setIsLoading(false);

      await Alert({
        icon: "error",
        title: "Erro",
        message,
        showConfirmButton: true,
        confirmButtonText: "OK",
      });

      return;
    }
  };

  return (
    <div className={Style.container}>
      {isFornecedor && (
        <div className={Style.tabsContainer}>
          <button
            className={`${Style.tabButton} ${activeTab === "dados" ? Style.activeTab : ""
              }`}
            onClick={() => setActiveTab("dados")}
          >
            <User size={18} style={{ marginRight: 8 }} /> Dados
          </button>

          <button
            className={`${Style.tabButton} ${activeTab === "alteracao" ? Style.activeTab : ""
              }`}
            onClick={() => setActiveTab("alteracao")}
          >
            <Lock size={18} style={{ marginRight: 8 }} /> Alteração
          </button>
        </div>
      )}

      <div className={Style.tabContentWrapper}>
        {(!isFornecedor || activeTab === "dados") && <DadosTab user={user} />}
        {isFornecedor && activeTab === "alteracao" && (
          <AlteracaoTab
            passwordData={passwordData}
            handlePasswordChange={handlePasswordChange}
            handlePasswordSubmit={handlePasswordSubmit}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};
